/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useState } from "react";

import fetchWithAuth from "layouts/tables/data/api";
import appSettings from "appsettings.json";

function ErrorLogs() {
  const [logs, setLogs] = useState([]);
  const [logFiles, setLogFiles] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [downloadingFileName, setDownloadingFileName] = useState("");

  const normalizeLogs = (data) => {
    const list = data?.$values || data;
    if (!Array.isArray(list)) return [];

    return list.map((item) => {
      if (typeof item === "string") return item;

      if (item && typeof item === "object") {
        const timestamp = item.timestamp || item.Timestamp || "";
        const level = item.level || item.Level || "";
        const message = item.message || item.Message || "";
        const exception = item.exception || item.Exception || "";

        return [timestamp, level, message, exception].filter(Boolean).join(" | ");
      }

      return String(item);
    });
  };

  const loadErrorLogs = async () => {
    const endpoint = appSettings?.endpoints?.getErrorLogs;

    if (!endpoint) {
      setError("Error log endpoint is not configured in appsettings.json.");
      setLogs([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetchWithAuth(`${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`, {
        method: "GET",
        cache: "no-store",
      });
      if (!response.ok) {
        throw new Error("Failed to load error logs.");
      }

      const data = await response.json();
      const normalized = normalizeLogs(data);
      setLogs(normalized);

      if (normalized.length === 0) {
        setError("No logs were returned.");
      }
    } catch (err) {
      setLogs([]);
      setError(err.message || "An unexpected error occurred while loading logs.");
    } finally {
      setLoading(false);
    }
  };

  const normalizeLogFiles = (data) => {
    const list = data?.$values || data;
    if (!Array.isArray(list)) return [];

    return list
      .map((item) => ({
        fileName: item?.fileName || item?.FileName || "",
        sizeBytes: item?.sizeBytes ?? item?.SizeBytes ?? 0,
        lastWriteTimeUtc: item?.lastWriteTimeUtc || item?.LastWriteTimeUtc || "",
      }))
      .filter((item) => item.fileName);
  };

  const formatBytes = (value) => {
    const size = Number(value) || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  const loadErrorLogFiles = async () => {
    const endpoint = appSettings?.endpoints?.getErrorLogFiles;
    if (!endpoint) {
      setError("Error log files endpoint is not configured in appsettings.json.");
      setLogFiles([]);
      return;
    }

    setFilesLoading(true);
    setError("");

    try {
      const response = await fetchWithAuth(`${appSettings.baseUrl}${endpoint}?_ts=${Date.now()}`, {
        method: "GET",
        cache: "no-store",
      });
      if (!response.ok) {
        throw new Error("Failed to load error log files.");
      }

      const data = await response.json();
      setLogFiles(normalizeLogFiles(data));
    } catch (err) {
      setLogFiles([]);
      setError(err.message || "An unexpected error occurred while loading log files.");
    } finally {
      setFilesLoading(false);
    }
  };

  const parseDownloadFileName = (contentDisposition, fallbackName) => {
    if (!contentDisposition) return fallbackName;

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

    const simpleMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
    if (simpleMatch?.[1]) return simpleMatch[1];

    return fallbackName;
  };

  const handleDownloadLog = async (fileName) => {
    const endpoint = appSettings?.endpoints?.downloadErrorLog;
    if (!endpoint) {
      setError("Download endpoint is not configured in appsettings.json.");
      return;
    }

    setDownloadingFileName(fileName);
    setError("");

    try {
      const url = `${appSettings.baseUrl}${endpoint}?fileName=${encodeURIComponent(
        fileName
      )}&_ts=${Date.now()}`;
      const response = await fetchWithAuth(url, {
        method: "GET",
        cache: "no-store",
      });

      if (!response.ok) {
        let errorMessage = "Failed to download selected log file.";
        try {
          errorMessage = await response.text();
        } catch (e) {
          // Keep fallback message when response body is unavailable.
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("content-disposition");
      const downloadFileName = parseDownloadFileName(contentDisposition, fileName);

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err.message || "An unexpected error occurred while downloading log file.");
    } finally {
      setDownloadingFileName("");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDTypography variant="h6" color="white">
                  Error Logs
                </MDTypography>
              </MDBox>

              <MDBox p={3}>
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={loadErrorLogs}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Load Error Logs"}
                </MDButton>
                <MDButton
                  sx={{ ml: 1 }}
                  variant="outlined"
                  color="success"
                  onClick={loadErrorLogFiles}
                  disabled={filesLoading}
                >
                  {filesLoading ? "Loading Files..." : "Load Log Files"}
                </MDButton>

                {error ? (
                  <MDBox mt={2}>
                    <MDTypography variant="button" color="error">
                      {error}
                    </MDTypography>
                  </MDBox>
                ) : null}

                <MDBox mt={3}>
                  {logs.map((log, index) => (
                    <MDBox
                      key={`${index}-${log.slice(0, 20)}`}
                      p={1.5}
                      mb={1}
                      borderRadius="md"
                      bgColor="grey-100"
                    >
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{
                          fontFamily: "monospace",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {log}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>

                <MDBox mt={3}>
                  <MDTypography variant="button" fontWeight="bold" color="text">
                    Log Files
                  </MDTypography>
                  {logFiles.length === 0 ? (
                    <MDBox mt={1}>
                      <MDTypography variant="caption" color="text">
                        No log files loaded.
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <MDBox mt={1}>
                      {logFiles.map((file) => (
                        <MDBox
                          key={file.fileName}
                          p={1.5}
                          mb={1}
                          borderRadius="md"
                          border="1px solid"
                          borderColor="grey-300"
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          gap={2}
                        >
                          <MDBox>
                            <MDTypography variant="caption" color="text" fontWeight="bold">
                              {file.fileName}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              {formatBytes(file.sizeBytes)}
                              {file.lastWriteTimeUtc
                                ? ` | ${new Date(file.lastWriteTimeUtc).toLocaleString()}`
                                : ""}
                            </MDTypography>
                          </MDBox>
                          <Tooltip title="Download" placement="top">
                            <span>
                              <IconButton
                                color="primary"
                                size="small"
                                disabled={downloadingFileName === file.fileName}
                                onClick={() => handleDownloadLog(file.fileName)}
                              >
                                <Icon fontSize="small">download</Icon>
                              </IconButton>
                            </span>
                          </Tooltip>
                        </MDBox>
                      ))}
                    </MDBox>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ErrorLogs;
